/**
 * GlobalDatePicker — centralized rendering of DatePickerSheet.
 *
 * Subscribes to viewContext `schedulePicker` and `deadlinePicker` state
 * (set by project/list menus, sidebar context menu, action panel, etc.)
 * and renders a DatePickerSheet for the first taskId.
 *
 * Mounted once in ActionPanel alongside ContainerPicker and TagPicker.
 */

import { memo, useMemo } from "react"
import dayjs from "dayjs"
import { useViewContext } from "@/app/stores/viewContext"
import { useLiveQueryImmediate } from "@/db/hooks"
import { db } from "@/db/schema"
import { parseDbDate } from "@totodo/operations"
import type { DbObject, DbStartAtIndex, DbReminder } from "@/db/schema"
import { DatePickerSheet } from "./DatePickerSheet"

// ---------------------------------------------------------------------------
// Data hook — loads the task + startAtIndex + reminder for the active picker
// ---------------------------------------------------------------------------

function usePickerData(taskId: string | undefined) {
  const [data] = useLiveQueryImmediate(
    async () => {
      if (!taskId) return undefined
      const [task, startAtData, reminder] = await Promise.all([
        db.totodo.get(taskId) as Promise<DbObject | undefined>,
        db.startAtIndex.get(`#${taskId}`) as Promise<
          DbStartAtIndex | undefined
        >,
        db.reminder
          .where("taskId")
          .equals(taskId)
          .first() as Promise<DbReminder | undefined>,
      ])
      if (!task) return undefined
      return { task, startAtData, reminder }
    },
    [taskId],
    undefined,
  )
  return data
}

/**
 * Hook to load highlighted dates map for multi-select.
 * Returns Map<"YYYY-MM-DD", count> for all selected tasks' startAt dates.
 */
function useHighlightedDates(taskIds: string[] | undefined) {
  const [dates] = useLiveQueryImmediate(
    async () => {
      if (!taskIds || taskIds.length <= 1) return undefined
      const keys = taskIds.map((id) => `#${id}`)
      const items = await db.startAtIndex.bulkGet(keys)
      const map = new Map<string, number>()
      for (const item of items) {
        const startMs = parseDbDate(item?.startAt)
        if (startMs === undefined) continue
        const dateKey = dayjs(startMs).format("YYYY-MM-DD")
        map.set(dateKey, (map.get(dateKey) ?? 0) + 1)
      }
      return map.size > 0 ? map : undefined
    },
    [taskIds],
    undefined,
  )
  return dates
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const GlobalDatePicker = memo(function GlobalDatePicker() {
  const schedulePicker = useViewContext((s) => s.schedulePicker)
  const deadlinePicker = useViewContext((s) => s.deadlinePicker)

  const scheduleTaskId = schedulePicker?.taskIds[0]
  const deadlineTaskId = deadlinePicker?.taskIds[0]

  // Only one picker should be open at a time — schedule takes priority
  const activeTaskId = scheduleTaskId ?? deadlineTaskId
  const mode = scheduleTaskId ? "schedule" : "deadline"
  const isOpen = activeTaskId != null

  const data = usePickerData(activeTaskId)

  // Multi-select highlighted dates (only for schedule mode)
  const allTaskIds = useMemo(
    () => schedulePicker?.taskIds,
    [schedulePicker?.taskIds],
  )
  const highlightedDates = useHighlightedDates(allTaskIds)

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      useViewContext.getState().closePickers()
    }
  }

  // Don't render until we have the task data
  if (!isOpen || !data?.task) return null

  if (mode === "schedule") {
    return (
      <DatePickerSheet
        open
        onOpenChange={handleOpenChange}
        task={data.task}
        mode="schedule"
        startAtData={data.startAtData}
        existingReminder={data.reminder}
        highlightedDates={highlightedDates}
      />
    )
  }

  return (
    <DatePickerSheet
      open
      onOpenChange={handleOpenChange}
      task={data.task}
      mode="deadline"
      taskIds={deadlinePicker?.taskIds}
    />
  )
})
